import debounce from 'lodash/debounce';

import { scrollStateKey } from '../components/view-shell/constants';

const thresholdX = 3;
const thresholdY = 3;
const debounceTimeout = 100;
// The rAF-fed caller resets the trailing timer every frame, so without maxWait a continuous
// scroll would write NOTHING until the first 100ms pause. Input-initiated departures don't
// rely on this: the saver hook flushes the pending write on pointerdown/keydown, before the
// navigation the input triggers switches the entry (the flick-then-tap shape). maxWait
// bounds the loss for departures with no in-document input — browser chrome Back, script
// navigations — while staying well clear of WebKit's ~100 history writes per 30s budget
// (300ms would saturate it exactly and start dropping writes).
const debounceMaxWait = 1000;
// Suppression must outlive the restore loop by more than debounceTimeout: the loop's last
// scrollTo queues a saver rAF that re-arms the debounce AFTER the loop's finally ran.
const suppressionTailMs = 150;

let scrollSaveSuppressed = false;
let suppressionTailTimer: number | null = null;
let suppressionTailRaf: number | null = null;
// Owner of the CURRENT suppression: a superseded restore's promise loop notices its abort
// only one frame late, so its finally must not lift the suppression the new restore holds.
let suppressionGeneration = 0;

/**
 * A scroll restore is driving the window: its programmatic scrollTo calls fire real scroll
 * events, and without suppression the saver would record the chase's transient positions
 * (maxWait flushes them mid-chase) over the real target. Returns the ownership token for
 * {@link endScrollRestoreSuppressionSoon}.
 */
const beginScrollRestoreSuppression = (): number => {
    if (suppressionTailTimer !== null) {
        window.clearTimeout(suppressionTailTimer);
        suppressionTailTimer = null;
    }
    if (suppressionTailRaf !== null) {
        window.cancelAnimationFrame(suppressionTailRaf);
        suppressionTailRaf = null;
    }
    scrollSaveSuppressed = true;
    storeScrollPosition.cancel();
    return ++suppressionGeneration;
};

/**
 * Lifts the suppression after a tail delay — an rAF the loop's last write queued can still
 * re-arm the debounce after the restore's finally, and that trailing write must be dropped
 * too. No-ops unless `generation` still owns the suppression (a newer restore re-began it).
 *
 * A hidden tab defers the tail to the first rendering update instead of wall-clock: hidden
 * documents skip rendering, so the chase's writes fired no scroll events yet — they sit
 * coalesced until the tab returns, while background throttling still lets a plain timeout
 * expire. The deferred flush would then land with suppression already lifted and overwrite
 * the exit-persisted target with the parked clamp. rAF fires in that same first rendering
 * update (scroll steps run before animation frames, and the saver's own rAF + trailing
 * debounce push its write past a tail armed there), so the deferred flush stays suppressed.
 */
const endScrollRestoreSuppressionSoon = (generation: number) => {
    if (generation !== suppressionGeneration) {
        return;
    }
    if (suppressionTailTimer !== null) {
        window.clearTimeout(suppressionTailTimer);
        suppressionTailTimer = null;
    }
    if (suppressionTailRaf !== null) {
        window.cancelAnimationFrame(suppressionTailRaf);
        suppressionTailRaf = null;
    }
    const armTail = () => {
        suppressionTailTimer = window.setTimeout(() => {
            suppressionTailTimer = null;
            scrollSaveSuppressed = false;
        }, suppressionTailMs);
    };
    if (document.hidden) {
        suppressionTailRaf = window.requestAnimationFrame(() => {
            suppressionTailRaf = null;
            if (generation !== suppressionGeneration) {
                return; // a newer restore began while the tab was hidden
            }
            armTail();
        });
    } else {
        armTail();
    }
};

const getScrollState = (state: unknown) => {
    if (!state || typeof state !== 'object') {
        return null;
    }
    const value = (state as Record<string, unknown>)[scrollStateKey];
    if (!value || typeof value !== 'object') {
        return null;
    }
    const { x, y } = value as { x?: unknown; y?: unknown };
    if (typeof x !== 'number' || typeof y !== 'number') {
        return null;
    }
    return { x, y };
};

const updateScrollPositionInHistory = (x: number, y: number) => {
    const historyState = window.history.state || {};
    const existingState = (historyState.state as Record<string, unknown> | undefined) || {};

    const currentScroll = getScrollState(existingState);

    if (currentScroll && Math.abs(currentScroll.x - x) < thresholdX && Math.abs(currentScroll.y - y) < thresholdY) {
        return;
    }
    const nextState = {
        ...existingState,
        [scrollStateKey]: { x, y },
    };
    const nextHistoryState = {
        ...historyState,
        state: nextState,
    };
    try {
        window.history.replaceState(nextHistoryState, '');
    } catch {
        // WebKit throws SecurityError past ~100 history writes per 30s. The saver's maxWait
        // keeps its own rate clear of that, but the budget is shared with every other writer
        // (router replaces, restore seeds). The save is best-effort: a skipped write must not
        // take down the caller (the saver's timer, the saver hook's effect, the restore's
        // seed/exit persists); a later write records the position once the budget refills.
    }
};

const storeScrollPosition = debounce(
    () => {
        if (scrollSaveSuppressed) {
            return;
        }
        updateScrollPositionInHistory(window.scrollX, window.scrollY);
    },
    debounceTimeout,
    { maxWait: debounceMaxWait },
);

/**
 * Writes the given position into the current history entry, dropping any pending debounced
 * live-position write. Used when a scroll restore gives up before reaching its target: the
 * live position at that moment is a clamped best-effort one, and letting it overwrite the
 * entry would make the real target unrecoverable for the next back/forward visit.
 */
const persistScrollPosition = (x: number, y: number) => {
    storeScrollPosition.cancel();
    updateScrollPositionInHistory(x, y);
};

export { beginScrollRestoreSuppression, endScrollRestoreSuppressionSoon, getScrollState, persistScrollPosition, storeScrollPosition };
